/* eslint-disable react-hooks/exhaustive-deps */
import { useRef, useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTitle, Box, useDisclosure } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useMsal } from '@azure/msal-react';

import { Alert, AlertIcon, Text } from 'components/v4';
import ChakraButton from 'components/v4/Button';
import AddProvider from './AddProvider';
import { initialState } from './constants';
import EditMicrosoftSSO from './EditMicrosoftSSO';
import walkthroughIds from '../../../accounts/admins/walkthroughIds';
import { loginRequest } from 'pages/auth/new/sso/config/authConfig';
import { isPopupBlockedError } from 'pages/auth/new/sso/utils/msLogin';
import getSSOProvider from '@/API/SSO/getSSOProvider';
import useGetSSOConfig from './queries/useGetSSOConfig';
import useSaveSSOConfig from './queries/useSaveSSOConfig';
import useUpdateSSOProvider from './queries/useUpdateSSOProvider';
import useVerifySSOProvider from './queries/useVerifySSOProvider';
import SSOTableToolbar from './components/SSOTableToolbar';
import SSOLoadingSkeleton from './components/SSOLoadingSkeleton';
import SSOStatusAlert from './components/SSOStatusAlert';
import SSOProvidersTable from './components/SSOProvidersTable';

export default function SSO() {
    const isDevelopment = import.meta.env.DEV;

    const { darkMode } = useSelector((state) => state.settings);

    const [isChanged, setIsChanged] = useState(false);
    const [switchDisabled, setSwitchDisabled] = useState(false);

    const {
        isOpen: isOpenProvider,
        onOpen: onOpenProvider,
        onClose: onCloseProvider,
    } = useDisclosure();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });
    const gridRef = useRef(null);
    const { instance } = useMsal();

    const { ufProviderBtn, ufSaveBtn, ufVerifyBtn, ufActiveBtn } =
        walkthroughIds.adminSSO;
    const ufEnforceSSO = 'accounts/admins/sso/switch/enforce';

    const [ssoConfig, setSSOConfig] = useState(initialState);

    // Queries & mutations
    const {
        data: ssoData,
        isLoading,
        refetch: refetchSSOConfig,
    } = useGetSSOConfig(currentCompany, {
        enabled: !isOpen && !isOpenProvider,
    });

    const queryClient = useQueryClient();
    const saveMutation = useSaveSSOConfig();
    const updateProviderMutation = useUpdateSSOProvider();
    const verifyMutation = useVerifySSOProvider();

    // Sync query data into local state (needed for optimistic enforce toggle)
    useEffect(() => {
        if (ssoData) {
            setSSOConfig(ssoData);
            setIsChanged(false);
        }
    }, [ssoData]);

    // Clear the mirrored config as soon as the managed company changes. A
    // company with no SSO setup answers with an empty body, so the sync effect
    // above has nothing to write and the previous company's config would stay
    // on show — its providers listed, and Add Microsoft SSO disabled for a
    // company that has none. Done during render rather than in an effect so
    // the previous company's providers are never rendered, and so a mount is
    // not treated as a switch.
    const [companyAtLastReset, setCompanyAtLastReset] =
        useState(currentCompany);

    if (companyAtLastReset !== currentCompany) {
        setCompanyAtLastReset(currentCompany);
        setSSOConfig(initialState);
        setIsChanged(false);
    }

    const hasActiveProvider = ssoConfig.providers?.some(
        (p) => p.isVerified && p.isEnabled,
    );

    const isMicrosoftAdded = ssoConfig.providers?.some((p) => p.provider === 0);

    const handleEnforceCheckbox = () => {
        setIsChanged((p) => !p);
        setSSOConfig((prevState) => ({
            ...prevState,
            isEnforced: !prevState.isEnforced,
        }));
    };

    const handleOnSave = () => {
        const sso = {
            isEnabled: hasActiveProvider || false,
            isEnforced: ssoConfig?.isEnforced || false,
            companyID: currentCompany,
        };
        if (ssoConfig?.id) {
            sso.id = ssoConfig.id;
        }
        saveMutation.mutate(sso, {
            onSuccess: (data) => {
                setIsChanged(false);
                setSSOConfig((prev) => ({
                    ...prev,
                    isEnabled: data?.isEnabled ?? sso.isEnabled,
                    isEnforced: data?.isEnforced ?? sso.isEnforced,
                    ...(data?.id && !prev.id ? { id: data.id } : {}),
                }));
            },
        });
    };

    const handleVerify = useCallback(async () => {
        try {
            let redirectUri;
            const apiBaseUrl =
                import.meta.env.VITE_APP_API_URL ||
                `${window.location.origin}/api/ui`;
            const configuredApiHost = (() => {
                try {
                    return new URL(apiBaseUrl).host;
                } catch {
                    return null;
                }
            })();
            const requestHost =
                import.meta.env.MODE === 'staging'
                    ? 'launch.tcap.dev'
                    : isDevelopment
                      ? 'teamsdev.tcap.dev'
                      : configuredApiHost || window.location.host;
            const domainEndpoint = `${apiBaseUrl}/domain`;

            let result;
            try {
                const response = await fetch(domainEndpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-RequestScope': '20',
                    },
                    body: JSON.stringify(requestHost),
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    toast.error(
                        errorText || 'Unable to resolve SSO domain reference.',
                    );
                    return;
                }

                const responseText = await response.text();
                const normalizedResponse = responseText.trim();
                result =
                    normalizedResponse.startsWith('"') &&
                    normalizedResponse.endsWith('"')
                        ? JSON.parse(normalizedResponse)
                        : normalizedResponse;
            } catch (error) {
                console.error('SSO domain lookup failed before verification:', {
                    domainEndpoint,
                    requestHost,
                    error,
                });
                toast.error(
                    error?.message ||
                        'Failed to call the SSO domain lookup endpoint.',
                );
                return;
            }

            let decodedReference;
            try {
                decodedReference = window.atob(result).split('|');
            } catch (error) {
                console.error('Unable to decode SSO domain lookup response:', {
                    result,
                    error,
                });
                toast.error('Received an invalid SSO domain lookup response.');
                return;
            }

            const referenceID = decodedReference[0];

            if (isDevelopment) {
                redirectUri = `${window.location.origin}/ui/sso/ms/redirect`;
            } else if (window.location.host == 'teamsdev.tcap.dev') {
                redirectUri = `https://${window.location.host}/ui/sso/ms/redirect`;
            } else {
                redirectUri = `https://${decodedReference[1]}/ui/sso/ms/redirect`;
            }

            const login = await instance
                .loginPopup({
                    ...loginRequest,
                    redirectUri: redirectUri,
                    state: referenceID,
                })
                .catch((e) => {
                    const isPopupBlocked =
                        isPopupBlockedError(e) ||
                        e?.message?.toLowerCase().includes('popup');
                    if (isPopupBlocked) {
                        toast.error(
                            'The verification popup was blocked by your browser. Please allow popups for this site and try again.',
                        );
                    } else if (e?.errorCode === 'user_cancelled') {
                        toast.warning(
                            'Verification was cancelled. Please complete the Microsoft sign-in to verify.',
                        );
                    } else if (e) {
                        toast.error(
                            'Microsoft sign-in failed. Please try again.',
                        );
                    }
                    return null;
                });

            if (login) {
                const encodedToken = window.btoa(login.accessToken);
                verifyMutation.mutate(
                    { accessToken: encodedToken, ssoid: ssoConfig?.id },
                    {
                        onSuccess: (data) => {
                            setSSOConfig((prevState) => ({
                                ...prevState,
                                providers: prevState.providers.map((p) =>
                                    p.provider === 0 ? data : p,
                                ),
                            }));
                            refetchSSOConfig();
                        },
                    },
                );
            }
        } catch (e) {
            console.error('Unexpected SSO verification failure:', e);
            toast.error(
                e?.message || 'Unable to verify provider. Please try again.',
            );
        }
    }, [
        instance,
        isDevelopment,
        ssoConfig?.id,
        refetchSSOConfig,
        ssoConfig.providers,
        verifyMutation,
    ]);

    async function handleSwitchChange(e) {
        setSwitchDisabled(true);

        // rowIndex is 1-based for providers because TCAP username & password occupies index 0 now
        const providerIndex = e.target.id - 1;
        const updatedProviders = [...ssoConfig.providers];
        const isEnabled = e.target.checked;

        try {
            // Fetch full provider detail to preserve isBoth/tenantIds/emailDomains,
            // which are not included in the list-level GET /sso/{companyId} response.
            const fullProv = await queryClient.fetchQuery({
                queryKey: ['ssoProvider', updatedProviders[providerIndex].id],
                queryFn: () =>
                    getSSOProvider(updatedProviders[providerIndex].id),
                staleTime: 0,
            });
            const editProv = {
                ...fullProv,
                isEnabled,
                companyID: currentCompany,
            };

            updateProviderMutation.mutate(editProv, {
                onSuccess: () => {
                    setSSOConfig((prevState) => {
                        const updatedProvidersState = [...prevState.providers];
                        updatedProvidersState[providerIndex] = {
                            ...prevState.providers[providerIndex],
                            isEnabled,
                        };

                        if (
                            updatedProviders.some(
                                (a) => a.isVerified && a.isEnabled,
                            )
                        ) {
                            return {
                                ...prevState,
                                isEnabled: false,
                                isEnforced: false,
                                providers: updatedProvidersState,
                            };
                        }

                        return {
                            ...prevState,
                            providers: updatedProvidersState,
                        };
                    });
                    // if provider isEnabled change from false to true
                    // notify user that change detected, need to save
                    if (isChanged && hasActiveProvider) {
                        setIsChanged(true);
                    }
                },
                onSettled: () => setSwitchDisabled(false),
            });
        } catch {
            setSwitchDisabled(false);
        }
    }

    function handleProviderSelection() {
        onOpenProvider();
    }

    const tableToolbar = (
        <SSOTableToolbar
            isMicrosoftAdded={isMicrosoftAdded}
            hasActiveProvider={hasActiveProvider}
            ssoIsEnabled={ssoConfig.isEnabled}
            isEnforced={ssoConfig.isEnforced}
            onAddProvider={handleProviderSelection}
            onEnforceChange={handleEnforceCheckbox}
            ufProviderBtn={ufProviderBtn}
            ufEnforceSSO={ufEnforceSSO}
        />
    );

    if (!currentCompany) {
        return (
            <Alert status="error">
                <AlertIcon />
                <AlertTitle>
                    No Partner / Company / Wholesaler Selected
                </AlertTitle>
                <Text>Please select a wholesaler and a partner / company.</Text>
            </Alert>
        );
    }

    return (
        <>
            {isLoading ? (
                <SSOLoadingSkeleton darkMode={darkMode} />
            ) : (
                <>
                    <SSOStatusAlert isChanged={isChanged} />
                    <SSOProvidersTable
                        providers={ssoConfig.providers}
                        gridRef={gridRef}
                        refetch={refetchSSOConfig}
                        toolbar={tableToolbar}
                        isEnforced={ssoConfig.isEnforced}
                        darkMode={darkMode}
                        switchDisabled={switchDisabled}
                        verifyMutationPending={verifyMutation.isPending}
                        ufVerifyBtn={ufVerifyBtn}
                        ufActiveBtn={ufActiveBtn}
                        handleVerify={handleVerify}
                        handleSwitchChange={handleSwitchChange}
                        onOpen={onOpen}
                    />
                </>
            )}

            {/* Save button */}
            <Box display="flex" justifyContent="flex-start" mt={6}>
                <ChakraButton
                    data-walkthroughid={ufSaveBtn}
                    isDisabled={isLoading || !hasActiveProvider}
                    isLoading={saveMutation.isPending}
                    onClick={handleOnSave}
                    sx={{ width: 142, height: '40px' }}>
                    Save
                </ChakraButton>
            </Box>

            <AddProvider
                isOpen={isOpenProvider}
                onClose={onCloseProvider}
                ssoProviders={ssoConfig.providers?.map((prov) => prov.provider)}
            />

            <EditMicrosoftSSO
                isOpen={isOpen}
                onClose={onClose}
                providerId={
                    ssoConfig.providers?.find((a) => a.provider == 0)?.id
                }
            />
        </>
    );
}
